import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { serverUrl } from "../../../Utils/server";

const defaultConfig = {
  enabled: true,
  maxActiveNotices: 2,
  maxNoticeDurationSeconds: 15,
  maxNoticeSizeBytes: 25 * 1024 * 1024,
  maxPermanentVideos: 2,
  maxMediaDurationSeconds: 60,
  maxMediaSizeBytes: 50 * 1024 * 1024,
  qualityProfile: "medium"
};

const planPresets = {
  free: {
    enabled: true,
    maxActiveNotices: 2,
    maxNoticeDurationSeconds: 15,
    maxNoticeSizeBytes: 25 * 1024 * 1024,
    maxPermanentVideos: 2,
    maxMediaDurationSeconds: 60,
    maxMediaSizeBytes: 50 * 1024 * 1024,
    qualityProfile: "medium"
  },
  pro: {
    enabled: true,
    maxActiveNotices: 5,
    maxNoticeDurationSeconds: 30,
    maxNoticeSizeBytes: 50 * 1024 * 1024,
    maxPermanentVideos: 10,
    maxMediaDurationSeconds: 180,
    maxMediaSizeBytes: 100 * 1024 * 1024,
    qualityProfile: "high"
  },
  premium: {
    enabled: true,
    maxActiveNotices: 10,
    maxNoticeDurationSeconds: 60,
    maxNoticeSizeBytes: 100 * 1024 * 1024,
    maxPermanentVideos: 30,
    maxMediaDurationSeconds: 300,
    maxMediaSizeBytes: 250 * 1024 * 1024,
    qualityProfile: "high"
  }
};

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!value) return "—";
  return `${Math.round(value / 1024 / 1024)} MB`;
}

function normalizeConfig(source = {}) {
  return {
    enabled: source.enabled !== false,
    maxActiveNotices: toNumber(source.maxActiveNotices, defaultConfig.maxActiveNotices),
    maxNoticeDurationSeconds: toNumber(source.maxNoticeDurationSeconds, defaultConfig.maxNoticeDurationSeconds),
    maxNoticeSizeBytes: toNumber(source.maxNoticeSizeBytes, defaultConfig.maxNoticeSizeBytes),
    maxPermanentVideos: toNumber(source.maxPermanentVideos, defaultConfig.maxPermanentVideos),
    maxMediaDurationSeconds: toNumber(source.maxMediaDurationSeconds, defaultConfig.maxMediaDurationSeconds),
    maxMediaSizeBytes: toNumber(source.maxMediaSizeBytes, defaultConfig.maxMediaSizeBytes),
    qualityProfile: ["low", "medium", "high"].includes(String(source.qualityProfile || ""))
      ? String(source.qualityProfile)
      : defaultConfig.qualityProfile
  };
}

function statusLabel(status) {
  const labels = {
    queued: "En cola",
    processing: "Procesando",
    ready: "Listo",
    active: "Activo",
    paused: "Pausado",
    expired: "Vencido",
    rejected: "Rechazado",
    deleted: "Eliminado",
    failed: "Fallido",
    pending: "Pendiente"
  };
  return labels[status] || status || "Pendiente";
}

function moderationLabel(value) {
  const labels = {
    pending: "Pendiente",
    pending_review: "Pendiente",
    approved: "Aprobado",
    auto_approved: "Auto aprobado",
    rejected: "Rechazado"
  };
  return labels[value] || value || "Pendiente";
}

function panelStyles() {
  return {
    shell: { display: "grid", gap: "1rem", marginTop: "1rem" },
    hero: {
      borderRadius: "1.4rem",
      padding: "1.25rem",
      color: "#fff",
      background: "linear-gradient(135deg, #020617, #111827 55%, #064e3b)",
      boxShadow: "0 18px 44px rgba(15, 23, 42, 0.18)"
    },
    kicker: {
      color: "#6ee7b7",
      fontWeight: 900,
      fontSize: "0.72rem",
      letterSpacing: "0.18em",
      textTransform: "uppercase"
    },
    h3: { margin: "0.35rem 0 0", fontSize: "1.45rem", fontWeight: 950 },
    p: { margin: "0.45rem 0 0", color: "#cbd5e1", lineHeight: 1.55 },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.8rem" },
    card: {
      border: "1px solid rgba(15, 23, 42, 0.08)",
      borderRadius: "1.15rem",
      padding: "1rem",
      background: "linear-gradient(180deg, #ffffff, #f8fafc)",
      boxShadow: "0 14px 32px rgba(15, 23, 42, 0.07)"
    },
    label: {
      display: "block",
      fontSize: "0.72rem",
      fontWeight: 900,
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.06em"
    },
    value: { display: "block", marginTop: "0.25rem", color: "#020617", fontSize: "1.4rem", fontWeight: 950 },
    inputGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "0.8rem" },
    input: {
      width: "100%",
      height: "2.7rem",
      border: "1px solid #dbe3ef",
      borderRadius: "0.8rem",
      padding: "0 0.85rem",
      fontWeight: 750,
      outline: "none",
      background: "#f8fafc"
    },
    buttonPrimary: {
      border: 0,
      borderRadius: "0.9rem",
      padding: "0.85rem 1.1rem",
      fontWeight: 900,
      color: "#020617",
      background: "linear-gradient(135deg, #fde047, #facc15)",
      cursor: "pointer"
    },
    buttonDark: {
      border: 0,
      borderRadius: "0.9rem",
      padding: "0.75rem 1rem",
      fontWeight: 900,
      color: "#fff",
      background: "#020617",
      cursor: "pointer"
    },
    buttonLight: {
      border: "1px solid #dbe3ef",
      borderRadius: "0.9rem",
      padding: "0.7rem 0.9rem",
      fontWeight: 900,
      color: "#0f172a",
      background: "#fff",
      cursor: "pointer"
    },
    pill: {
      display: "inline-flex",
      borderRadius: "999px",
      padding: "0.35rem 0.65rem",
      fontWeight: 900,
      fontSize: "0.72rem",
      background: "#ecfdf5",
      color: "#047857"
    },
    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: "0 0.5rem" },
    th: {
      textAlign: "left",
      fontSize: "0.72rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "#f8fafc",
      background: "linear-gradient(135deg, #020617, #111827)",
      padding: "0.9rem 0.75rem",
      fontWeight: 950,
      borderTop: "1px solid rgba(148, 163, 184, 0.24)",
      borderBottom: "1px solid rgba(148, 163, 184, 0.18)"
    },
    td: {
      background: "#ffffff",
      padding: "0.85rem 0.75rem",
      fontWeight: 800,
      color: "#0f172a",
      borderTop: "1px solid #e5e7eb",
      borderBottom: "1px solid #e5e7eb"
    },
    alert: {
      borderRadius: "1rem",
      padding: "0.9rem 1rem",
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      color: "#9a3412",
      fontWeight: 800
    },
    error: {
      borderRadius: "1rem",
      padding: "0.9rem 1rem",
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#991b1b",
      fontWeight: 800
    }
  };
}


function getMediaPreviewUrl(row = {}) {
  return row.optimizedUrl || row.hlsMasterUrl || row.originalUrl || "";
}

function getMediaPosterUrl(row = {}) {
  return row.thumbnailUrl || "";
}

function isImageMedia(row = {}) {
  const mediaType = String(row.mediaType || "").toLowerCase();
  const url = getMediaPreviewUrl(row).toLowerCase();
  return mediaType === "image" || url.includes(".jpg") || url.includes(".jpeg") || url.includes(".png") || url.includes(".webp");
}

export default function MerchantMediaAdminPanel({ merchantId, merchantName, authConfig, visible }) {
  const styles = useMemo(panelStyles, []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyModeration, setBusyModeration] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(defaultConfig);

  const auth = authConfig || {};

  const load = useCallback(async () => {
    if (!merchantId || !visible) return;

    setLoading(true);
    setError("");

    try {
      const response = await axios.get(`${serverUrl}/merchant-media/admin/merchant/${merchantId}`, auth);
      const payload = response.data || {};
      setData(payload);
      setConfig(normalizeConfig(payload.config?.limits || payload.limits || payload.mediaConfig || payload.config || {}));
    } catch (err) {
      const message =
        err?.response?.data?.msg ||
        err?.response?.data ||
        err?.message ||
        "No se pudo cargar Multimedia / Vitrina Viva del comercio.";
      setError(String(message));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [merchantId, visible]);

  useEffect(() => {
    load();
  }, [load]);

  if (!visible) return null;

  const items = Array.isArray(data?.items) ? data.items : [];
  const notices = Array.isArray(data?.notices) ? data.notices : [];
  const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
  const metricsCount = Number(data?.metricsCount || 0);

  const activeNotices = notices.filter((item) => item.status === "active").length;
  const activeVideos = items.filter((item) => item.status === "active").length;
  const processingCount = jobs.filter((job) => ["queued", "processing"].includes(String(job.status))).length;

  const updateConfig = (field, value) => {
    setConfig((current) => ({ ...current, [field]: value }));
  };

  const applyPreset = (presetKey) => {
    setConfig(planPresets[presetKey] || planPresets.free);
  };

  const saveConfig = async () => {
    if (!merchantId) {
      setError("Selecciona un comercio antes de guardar configuración multimedia.");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      enabled: Boolean(config.enabled),
      maxActiveNotices: toNumber(config.maxActiveNotices, defaultConfig.maxActiveNotices),
      maxNoticeDurationSeconds: toNumber(config.maxNoticeDurationSeconds, defaultConfig.maxNoticeDurationSeconds),
      maxNoticeSizeBytes: toNumber(config.maxNoticeSizeBytes, defaultConfig.maxNoticeSizeBytes),
      maxPermanentVideos: toNumber(config.maxPermanentVideos, defaultConfig.maxPermanentVideos),
      maxMediaDurationSeconds: toNumber(config.maxMediaDurationSeconds, defaultConfig.maxMediaDurationSeconds),
      maxMediaSizeBytes: toNumber(config.maxMediaSizeBytes, defaultConfig.maxMediaSizeBytes),
      qualityProfile: ["low", "medium", "high"].includes(String(config.qualityProfile))
        ? config.qualityProfile
        : "medium"
    };

    try {
      const response = await axios.patch(
        `${serverUrl}/merchant-media/admin/merchant/${merchantId}/config`,
        payload,
        auth
      );

      const saved = response.data?.config || response.data?.limits || payload;
      setConfig(normalizeConfig(saved));
      await load();
      window.alert("Configuración multimedia guardada correctamente.");
    } catch (err) {
      const message =
        err?.response?.data?.msg ||
        err?.response?.data ||
        err?.message ||
        "No se pudo guardar configuración multimedia.";
      setError(String(message));
    } finally {
      setSaving(false);
    }
  };

  const moderate = async (targetType, targetId, action) => {
    if (!targetId) return;

    const label = action === "approve" ? "aprobar" : "rechazar";
    const reason =
      action === "reject"
        ? window.prompt("Motivo de rechazo:", "Contenido no aprobado por administración.")
        : "";

    if (action === "reject" && !reason) return;
    if (!window.confirm(`Confirmar ${label} contenido multimedia.`)) return;

    setBusyModeration(`${targetType}:${targetId}`);
    setError("");

    try {
      await axios.patch(
        `${serverUrl}/merchant-media/admin/moderation/${targetType}/${targetId}`,
        { action, reason: reason || "" },
        auth
      );
      await load();
    } catch (err) {
      const message =
        err?.response?.data?.msg ||
        err?.response?.data ||
        err?.message ||
        "No se pudo actualizar moderación.";
      setError(String(message));
    } finally {
      setBusyModeration("");
    }
  };

  const renderRows = (rows, type) => {
    if (!rows.length) {
      return (
        <tr>
          <td style={styles.td} colSpan="6">Sin registros todavía.</td>
        </tr>
      );
    }

    return rows.map((row) => {
      const id = row.id || row._id;
      const busy = busyModeration === `${type}:${id}`;
      const targetType = type === "notice" ? "notice" : "media_item";
      const previewUrl = getMediaPreviewUrl(row);
      const posterUrl = getMediaPosterUrl(row);
      const canPreview = Boolean(previewUrl);

      return (
        <tr key={`${type}-${id}`}>
          <td style={styles.td}>
            <strong>{row.title || (type === "notice" ? "Aviso Comercial" : "Video del Comercio")}</strong>
            <br />
            <small>{row.description || "Sin descripción"}</small>
          </td>
          <td style={styles.td}>{statusLabel(row.status)}</td>
          <td style={styles.td}>{statusLabel(row.processingStatus)}</td>
          <td style={styles.td}>{moderationLabel(row.moderationStatus)}</td>
          <td style={styles.td}>
            {canPreview ? (
              <div style={{
                width: 220,
                maxWidth: "100%",
                border: "1px solid #cbd5e1",
                borderRadius: 16,
                overflow: "hidden",
                background: "#020617",
                boxShadow: "0 12px 30px rgba(15,23,42,0.14)"
              }}>
                {isImageMedia(row) ? (
                  <img
                    src={previewUrl}
                    alt={row.title || "Contenido multimedia"}
                    style={{ width: "100%", height: 124, objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <video
                    src={previewUrl}
                    poster={posterUrl}
                    controls
                    playsInline
                    preload="metadata"
                    style={{ width: "100%", height: 144, objectFit: "contain", display: "block", background: "#020617" }}
                  />
                )}

                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  padding: "0.45rem",
                  background: "#f8fafc"
                }}>
                  <span style={{ fontSize: 11, fontWeight: 950, color: "#0f172a" }}>
                    {row.optimizedUrl ? "Optimizado" : "Original"}
                  </span>
                  <a href={previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 950, color: "#2563eb" }}>
                    Abrir
                  </a>
                </div>
              </div>
            ) : (
              <span style={{ color: "#f59e0b", fontWeight: 950 }}>Pendiente Motor Media</span>
            )}
          </td>
          <td style={styles.td}>
            <button
              type="button"
              style={styles.buttonLight}
              disabled={busy || !canPreview}
              onClick={() => moderate(targetType, id, "approve")}
              title={!canPreview ? "No se puede aprobar sin archivo reproducible" : "Aprobar contenido"}
            >
              Aprobar
            </button>{" "}
            <button type="button" style={styles.buttonLight} disabled={busy} onClick={() => moderate(targetType, id, "reject")}>
              Rechazar
            </button>
          </td>
        </tr>
      );
    });
  };

  return (
    <div style={styles.shell}>
      <section style={styles.hero}>
        <span style={styles.kicker}>Vitrina Viva Admin</span>
        <h3 style={styles.h3}>Multimedia Comercial</h3>
        <p style={styles.p}>
          Configuración, límites, estado de procesamiento y moderación de contenido para{" "}
          <strong>{merchantName || "comercio seleccionado"}</strong>. Este panel no modifica Finanzas/Pagos.
        </p>
      </section>

      {!merchantId ? <div style={styles.alert}>Selecciona un comercio para administrar Vitrina Viva.</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}
      {loading ? <div style={styles.alert}>Cargando configuración multimedia...</div> : null}

      <section style={styles.grid}>
        <article style={styles.card}>
          <span style={styles.label}>Avisos activos</span>
          <strong style={styles.value}>{activeNotices}/{config.maxActiveNotices}</strong>
          <small>Duración máxima: {config.maxNoticeDurationSeconds}s · {formatBytes(config.maxNoticeSizeBytes)}</small>
        </article>

        <article style={styles.card}>
          <span style={styles.label}>Videos del Comercio</span>
          <strong style={styles.value}>{activeVideos}</strong>
          <small>Duración máxima: {config.maxMediaDurationSeconds}s · {formatBytes(config.maxMediaSizeBytes)}</small>
        </article>

        <article style={styles.card}>
          <span style={styles.label}>Procesamiento</span>
          <strong style={styles.value}>{processingCount}</strong>
          <small>Jobs en cola o proceso.</small>
        </article>

        <article style={styles.card}>
          <span style={styles.label}>Métricas</span>
          <strong style={styles.value}>{metricsCount}</strong>
          <small>Pulso Comercial registrado.</small>
        </article>
      </section>

      <section style={styles.card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
          <div>
            <span style={styles.pill}>{config.enabled ? "Multimedia habilitado" : "Multimedia deshabilitado"}</span>
            <h4 style={{ margin: "0.65rem 0 0", fontSize: "1.1rem", fontWeight: 950 }}>Plan y límites multimedia</h4>
            <p style={{ margin: "0.25rem 0 0", color: "#64748b", fontWeight: 700 }}>
              Estos límites alimentan PWA Comercio y validaciones de subida.
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
            <button type="button" style={styles.buttonLight} onClick={() => applyPreset("free")}>Free</button>
            <button type="button" style={styles.buttonLight} onClick={() => applyPreset("pro")}>Pro</button>
            <button type="button" style={styles.buttonLight} onClick={() => applyPreset("premium")}>Premium</button>
          </div>
        </div>

        <div style={{ ...styles.inputGrid, marginTop: "1rem" }}>
          <label>
            <span style={styles.label}>Estado</span>
            <select style={styles.input} value={config.enabled ? "active" : "inactive"} onChange={(event) => updateConfig("enabled", event.target.value === "active")}>
              <option value="active">Habilitado</option>
              <option value="inactive">Deshabilitado</option>
            </select>
          </label>

          <label>
            <span style={styles.label}>Calidad</span>
            <select style={styles.input} value={config.qualityProfile} onChange={(event) => updateConfig("qualityProfile", event.target.value)}>
              <option value="low">Baja</option>
              <option value="medium">Media</option>
              <option value="high">Alta</option>
            </select>
          </label>

          <label>
            <span style={styles.label}>Avisos activos máx.</span>
            <input style={styles.input} type="number" min="1" value={config.maxActiveNotices} onChange={(event) => updateConfig("maxActiveNotices", event.target.value)} />
          </label>

          <label>
            <span style={styles.label}>Duración aviso segundos</span>
            <input style={styles.input} type="number" min="1" value={config.maxNoticeDurationSeconds} onChange={(event) => updateConfig("maxNoticeDurationSeconds", event.target.value)} />
          </label>

          <label>
            <span style={styles.label}>Peso aviso bytes</span>
            <input style={styles.input} type="number" min="1" value={config.maxNoticeSizeBytes} onChange={(event) => updateConfig("maxNoticeSizeBytes", event.target.value)} />
          </label>

          <div style={{
            border: "1px solid #dbeafe",
            background: "#eff6ff",
            borderRadius: 18,
            padding: 14,
            color: "#1e3a8a",
            fontWeight: 850,
            fontSize: "0.82rem",
            lineHeight: 1.45
          }}>
            Videos del Comercio funciona como biblioteca comercial ilimitada.
            Los planes futuros monetizarán campañas activas, posiciones destacadas,
            prioridad, calidad, alcance y analítica; no la cantidad total de videos.
          </div>

          <label>
            <span style={styles.label}>Duración video segundos</span>
            <input style={styles.input} type="number" min="1" value={config.maxMediaDurationSeconds} onChange={(event) => updateConfig("maxMediaDurationSeconds", event.target.value)} />
          </label>

          <label>
            <span style={styles.label}>Peso video bytes</span>
            <input style={styles.input} type="number" min="1" value={config.maxMediaSizeBytes} onChange={(event) => updateConfig("maxMediaSizeBytes", event.target.value)} />
          </label>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <button type="button" style={styles.buttonPrimary} disabled={saving || !merchantId} onClick={saveConfig}>
            {saving ? "Guardando..." : "Guardar configuración multimedia"}
          </button>{" "}
          <button type="button" style={styles.buttonDark} disabled={loading || !merchantId} onClick={load}>
            Actualizar panel
          </button>
        </div>
      </section>

      <section style={styles.card}>
        <h4 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 950 }}>Avisos Comerciales 24h</h4>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Contenido</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Proceso</th>
                <th style={styles.th}>Moderación</th>
                <th style={styles.th}>Archivo</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>{renderRows(notices, "notice")}</tbody>
          </table>
        </div>
      </section>

      <section style={styles.card}>
        <h4 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 950 }}>Videos del Comercio</h4>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Contenido</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Proceso</th>
                <th style={styles.th}>Moderación</th>
                <th style={styles.th}>Archivo</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>{renderRows(items, "media_item")}</tbody>
          </table>
        </div>
      </section>

      <section style={styles.card}>
        <h4 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 950 }}>Motor Media / procesamiento</h4>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Calidad</th>
                <th style={styles.th}>Intentos</th>
                <th style={styles.th}>Error</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length ? jobs.map((job) => (
                <tr key={job.id || job._id}>
                  <td style={styles.td}>{job.targetType || job.mediaType || "media"}</td>
                  <td style={styles.td}>{statusLabel(job.status)}</td>
                  <td style={styles.td}>{job.qualityProfile || "medium"}</td>
                  <td style={styles.td}>{job.attemptCount || job.attempts || 0}</td>
                  <td style={styles.td}>{job.errorMessage || "—"}</td>
                </tr>
              )) : (
                <tr>
                  <td style={styles.td} colSpan="5">Sin jobs de procesamiento.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
